/**
 * Safely evaluates mathematical expressions without using eval()
 * Only allows basic mathematical operations: +, -, *, /, (), and numbers
 *
 * This used to delegate to mathjs, which cost ~171KB gzipped for the two
 * functions below — the only mathjs usage in the app, and mathjs is
 * tree-shake-hostile so effectively all of it shipped. The accepted grammar is
 * tiny (digits, `.`, `+ - * / ( )`, whitespace), so it is parsed directly here
 * instead. The security contract is unchanged and is pinned by
 * safeExpressionEvaluator.test.ts: character whitelist first, no identifiers /
 * calls / separators, and a non-finite result throws rather than leaking
 * Infinity or NaN.
 */

/**
 * Recursive-descent parser for:
 *   expr    := term (('+' | '-') term)*
 *   term    := factor (('*' | '/') factor)*
 *   factor  := ('+' | '-') factor | primary
 *   primary := number | '(' expr ')'
 *
 * Deliberately has no identifier, call, or separator production — an input
 * that could reference a function or symbol cannot parse, it can only throw.
 */
function parseArithmetic(input: string): number {
	let pos = 0;

	const skipWhitespace = () => {
		while (pos < input.length && /\s/.test(input[pos])) pos++;
	};

	const peek = (): string | undefined => {
		skipWhitespace();
		return input[pos];
	};

	const parseExpr = (): number => {
		let value = parseTerm();
		for (;;) {
			const op = peek();
			if (op !== "+" && op !== "-") return value;
			pos++;
			const rhs = parseTerm();
			value = op === "+" ? value + rhs : value - rhs;
		}
	};

	const parseTerm = (): number => {
		let value = parseFactor();
		for (;;) {
			const op = peek();
			if (op !== "*" && op !== "/") return value;
			pos++;
			const rhs = parseFactor();
			value = op === "*" ? value * rhs : value / rhs;
		}
	};

	const parseFactor = (): number => {
		const ch = peek();
		if (ch === "-") {
			pos++;
			return -parseFactor();
		}
		if (ch === "+") {
			pos++;
			return parseFactor();
		}
		return parsePrimary();
	};

	const parsePrimary = (): number => {
		const ch = peek();

		if (ch === "(") {
			pos++;
			const value = parseExpr();
			if (peek() !== ")") {
				throw new Error("Unbalanced parentheses");
			}
			pos++;
			return value;
		}

		const start = pos;
		while (pos < input.length && /[0-9.]/.test(input[pos])) pos++;
		if (pos === start) {
			throw new Error("Expected a number");
		}

		const literal = input.slice(start, pos);
		// Number() rejects multi-dot garbage like "1.2.3" (NaN) that a bare
		// parseFloat would silently truncate to 1.2.
		const value = Number(literal);
		if (!Number.isFinite(value)) {
			throw new Error(`Invalid number: ${literal}`);
		}
		return value;
	};

	const result = parseExpr();

	skipWhitespace();
	if (pos !== input.length) {
		throw new Error(`Unexpected token: ${input[pos]}`);
	}

	return result;
}

export function safeEvaluateExpression(expression: string): number {
	try {
		// First validate the expression contains only allowed characters
		const allowedPattern = /^[0-9.+\-*/()\s]+$/;
		if (!allowedPattern.test(expression)) {
			throw new Error("Expression contains invalid characters");
		}

		const result = parseArithmetic(expression);

		// Ensure the result is a number
		if (typeof result !== "number" || !Number.isFinite(result)) {
			throw new Error("Expression did not evaluate to a valid number");
		}

		return result;
	} catch (error) {
		throw new Error(
			`Invalid expression: ${error instanceof Error ? error.message : "Unknown error"}`,
		);
	}
}

/**
 * Validates if an expression is safe to evaluate
 */
export function isValidExpression(expression: string): boolean {
	try {
		safeEvaluateExpression(expression);
		return true;
	} catch {
		return false;
	}
}
